<template>
  <modal name="my-teacher-modal" @closed="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <page-title class="modal-title">My Teachers</page-title>
        <p class="modal-description">Your parents and teachers can help you get the most out of ReadTheory. <span class="invite-learn-more" @click="learnMoreVisible = !learnMoreVisible">Really, How? <rt-picture src="/assets/app/quiz/icon-dropdown.svg" /></span></p>
		<VueSlideToggle class="learnmore-container" :open="learnMoreVisible" tag="div" :duration="500">
			<div class="subtitle">Parents and teachers can do these awesome things...</div>
			<ul class="learnmore-list">
				<li>Track your reading progress</li>
				<li>Analyze your mastery of Common Core Standards</li>
				<li>Review your recent quizzes with you, side-by-side</li>
				<li>Include you in class progress reports</li>
				<li>Grade your written responses</li>
				<li>Reset your password if you forget it</li>
			</ul>
		</VueSlideToggle>
      </div>
      <div class="modal-body">
        <div class="update-container" v-if="mustProviderName">
            <p class="modal-description">Looks like we need a little more information to get you started. Please provide your name below, so that your teachers can identify you. (Remember, you can always update this information in your <a href="/user/manage">account settings</a>).</p>
            <div class="edit-wrapper">
              <rt-input class="modal-input" ref="firstName" :placeholder="`Real First Name ${coppaStudent ? 'Initial' : ''}`" :maxLength="coppaStudent ? 1 : 50" v-model="firstName"  />    
              <rt-input class="modal-input" ref="lastName" placeholder="Real Last Name" v-model="lastName"/>
            </div>
            <div class="submit-wrapper">
              <primary-button @click="updateInformation">
                Update Information
                <div class="loading-spinner" v-if="loading"></div>
              </primary-button>
            </div>
        </div>
        <div class="invite-container" v-else>
          <h2 class="sub-title">Enter your Class code or Teacher’s email </h2>
          <p class="modal-description">Input a class code given by your teacher to join the class or invite teacher to track your progress by entering their email in the space below.</p>
          <div class="input-wrapper">
            <rt-input class="modal-input" ref="inviteValue" placeholder="Class code or Teacher’s email" v-model="inviteValue"/>
            <div class="error-wrapper" v-if="submitError">
              <rt-picture src="/assets/app/class/icon-warning-white.svg"/>
              <span class="error-info">{{ submitError }}</span>
              <primary-button class="btn-normal btn-dismiss" @click="submitError = null">
                <rt-picture src="/assets/app/class/icon-closed.svg"/>
              </primary-button>
            </div>
            <div class="success-info-wrapper" v-if="submitSuccessInfo && submitSuccessInfo.joined">
              <avatar :username="submitSuccessInfo.className" />
              <div class="success-content">
                <p class="success-content-message">{{ submitSuccessInfo.message }}</p>
                <p class="success-content-description" v-html="submitSuccessInfo.description"></p>
              </div>
              <primary-button class="btn-normal btn-dismiss" @click="submitSuccessInfo = null">
                <rt-picture src="/assets/app/class/icon-closed.svg"/>
              </primary-button>
            </div>
          </div>
          <div class="submit-wrapper">
            <primary-button @click="sendRequest">
              Send request
              <div class="loading-spinner" v-if="loading"></div>
            </primary-button>
          </div>
          <div class="modal-separator"></div>
          <div class="teachers-wrapper" :class="{ empty: teachers.length === 0 }">
            <h2 class="sub-title">Current teachers</h2>
            <p class="modal-description">These teachers are currently tracking your progress</p>
            <ul class="teachers-list">
              <li class="teacher-item" :key="idx" v-for="(teacher, idx) in teachers">
                <div class="teacher-info">
                  <p class="teacher-name">{{ teacher.name }}</p>
                  <p class="teacher-email">{{ teacher.email }}</p>
                </div>
                <div class="invite-status" v-if="teacher.state === 'NoReply'">
                  <rt-picture src="/assets/app/quiz/icon-pending.svg"/>
                  <p class="invite-status-text">PENDING</p>
                </div>
                <div class="success-info-wrapper" v-if="!teacher.dismissed && teacher.replyDate && lastViewDatetime && teacher.replyDate > lastViewDatetime">
                  <avatar :username="teacher.name" />
                  <div class="success-content">
                    <p class="success-content-message">Teacher accepted your request</p>
                    <p class="success-content-description">
						Now <b>{{teacher.name}}</b> is tracking your progress
					</p>
                  </div>
                  <primary-button class="btn-normal btn-dismiss" @click="dismissTeacherInfo(teacher)">
                    <rt-picture src="/assets/app/class/icon-closed.svg"/>
                  </primary-button>
                </div>
              </li>
            </ul>
            <loading
              :active.sync="teacherLoading"
              :width="40"
              :height="40"
              color="#000"
              background-color="transparent"
              :is-full-page="false"
            />
          </div>
        </div>
      </div>
      <primary-button class="btn-normal btn-close" @click="closeModal">
        <rt-picture src="/assets/app/quiz/icon-close-blue.svg" />
      </primary-button>
		</div>
	</modal>
</template>

<script>
import Loading from 'vue-loading-overlay';
import Avatar from 'vue-avatar';
import { VueSlideToggle } from 'vue-slide-toggle'
import RtPicture from '../common/Picture.vue';
import RtInput from '../common/Input.vue';
import PageTitle from '../common/PageTitle.vue';
import PrimaryButton from '../common/PrimaryButton.vue';
import { validateEmail } from '../signup/validators';
import { isCoppaStudent } from 'common/coppa';
import { getUser } from 'common/UserService';
import { getTeachers, updateStudentRealname, inviteTeacher, joinClass } from 'common/api';
import Toast from 'common/Toast';

export default {
  props: ['show'],
  components: {
    Loading,
    Avatar,
	VueSlideToggle,
    RtPicture,
    RtInput,
    PageTitle,
    PrimaryButton
  },
  data: function() {
    return {
      teacherLoading: false,
      loading: false,
      learnMoreVisible: false,
	  coppaStudent: false,
      teachers: [],
      firstName: '',
      lastName: '',
      inviteValue: '',
      submitError: null,
      submitSuccessInfo: null,
      nameUpdated: false,
	  lastViewDatetime: null,
	  classCodeList: []
    }
  },
  computed: {
    mustProviderName() {
	  if (!this.nameUpdated) {
		const user = getUser();
		return user && (!user.firstName || !user.lastName);
	  } else {
		return false;
	  }
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$modal.show('my-teacher-modal');
        this.inviteValue = '';
        this.loadTeachers();
      }
    },
	firstName(newVal, oldVal) {
	  if (this.coppaStudent) {
		if (!!oldVal && oldVal.length === 2) {
		  this.firstName = '';
		} else if (!!newVal && newVal.length === 1) {
		  this.firstName = newVal.toUpperCase() + '.';
		}
	  }
	}
  },
  mounted() {
    const user = getUser();
	if (user) {
	  this.firstName = user.firstName;
      this.lastName = user.lastName;
	  this.coppaStudent = isCoppaStudent();
	}
  },
  methods: {
    async loadTeachers() {
      this.teacherLoading = true;
      const result = await getTeachers();
      if (result.success) {
        const data = result.data;
        let invites = [
          ...data.pendingInvites,
          ...data.currentInvites,
        ];
        if (invites.length > 0) {
          this.teachers = invites.map(item => {
            return {
              name: item.teacherListing,
              email: item.teacherEmail,
			  state: item.inviteState,
			  replyDate: item.replyStateDate ? new Date(item.replyStateDate).getTime() : null
            }
          });
        }
      } else {
        Toast.error('Cannot load teachers. Try again later');
      }
      this.teacherLoading = false;

	  const storedViewDatetime = localStorage.getItem('last_teachers_view_date');
	  if (storedViewDatetime) {
		this.lastViewDatetime = parseInt(storedViewDatetime);
	  } else {
		this.lastViewDatetime = Date.now();
		localStorage.setItem('last_teachers_view_date', this.lastViewDatetime);
	  }
    },
    hasSuccessForTeacher(teacher) {
      if (!this.submitSuccessInfo || (this.submitSuccessInfo && !this.submitSuccessInfo.inviteSent)) {
        return false;
      } else {
        return teacher.email === this.submitSuccessInfo.teacherEmail;
      }
    },
	dismissTeacherInfo(teacher) {
	  localStorage.setItem('last_teachers_view_date', Date.now());
	  let newTeachers = JSON.parse(JSON.stringify(this.teachers));
	  const teacherIndex = this.teachers.findIndex(item => item.email === teacher.email);
	  this.teachers = [
		...newTeachers.slice(0, teacherIndex),
		{
		   ...newTeachers.at(teacherIndex),
		   dismissed: true
		},
		...newTeachers.slice(teacherIndex + 1)
	  ]
	},
    async updateInformation() {
      if (this.loading) return;

	  if (!this.firstName) {
		this.firstName = ''
	  }
	  if (!this.lastName) {
		this.lastName = ''
	  }
	  this.loading = true;
	  const result = await updateStudentRealname(this.firstName, this.lastName);
	  if (result.success) {
		Toast.success(result.message);
		this.nameUpdated = true;
	  } else {
		Toast.error(result.message);
	  }
	this.loading = false;
    },
    async sendRequest() {
      if (this.loading) return;

      if (!this.inviteValue) {
        this.$refs.inviteValue.focus();
      } else {
        this.loading = true;
        const emailValid = (await validateEmail(this.inviteValue, false) === true);
        let result = null;
        if (emailValid) {
          result = await inviteTeacher(this.inviteValue);
        } else {
		  if (this.classCodeList.includes(this.inviteValue)) {
			this.loading = false;
			this.submitError = 'You have already joined to this class.';
			return;
		  }
          result = await joinClass(this.inviteValue);
        }
        if (result.success) {
          this.submitError = null;
          const successInfo = {};
		  successInfo.teacherEmail = result.teacherEmail;
		  successInfo.teacherName = result.teacherName;
          if (!emailValid) {
            // successInfo.inviteSent = true;
            // successInfo.teacherName = result.teacherName;
            // successInfo.message = 'Teacher accepted your request';
            // successInfo.description = `Now <b>${successInfo.teacherName}</b> is tracking your progress`;
            successInfo.joined = true;
            successInfo.className = result.className;
            successInfo.message = `You have joined to class: ${result.className}`;
            successInfo.description = `Teacher: <b>${result.teacherName}</b>`;
			this.classCodeList.push(this.inviteValue);
          }
		  const foundTeacher = this.teachers.find(item => item.email === successInfo.teacherEmail);
          if (!foundTeacher) {
			this.teachers = [
              {
			    name: successInfo.teacherName,
			    email: successInfo.teacherEmail,
			    state: emailValid ? 'NoReply' : 'Accepted'
              },
                ...this.teachers
			];
          }
          this.submitSuccessInfo = successInfo;
        } else {
          if (result.message.includes('Invalid')) {
            if (emailValid) {
              this.submitError = 'The email entered was not found in our system. Please try again.';
            } else {
              this.submitError = 'Invalid class code. Please try again.';
            }
          } else {
            this.submitError = result.message;
          }
        }
        this.loading = false;
      }
    },
    closeModal() {
      this.$modal.hide('my-teacher-modal');
      this.$emit('close', this.nameUpdated);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../common/variables';

.v--modal-overlay {
  background: rgba(20, 32, 53, 0.85) !important;
  z-index: 9999999999;
  ::v-deep(.v--modal-box) {
    background: transparent;
    box-shadow: none;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    .modal-container {
      position: fixed;
      right: 0;
      width: 570px;
      height: 100%;
      background: #fff;
      padding: 56px 64px;
      overflow-y: auto;
      transition: opacity 0.3s linear, right 0.3s ease-out;
      .modal-title {
        font-size: 30px;
        color: $title-dark-grey;
      }
      .modal-description {
        font-weight: 400;
        font-size: 14px;
        color: $title-grey;
        .invite-learn-more {
          font-weight: 700;
          color: $light-blue2;
          cursor: pointer;
        }
      }
      .learnmore-container {
        color: $title-grey;
        .learnmore-list {
          padding: 1em 2em 0;
          font-size: 14px;
        }
      }
      .submit-wrapper {
        margin-top: 16px;
        text-align: right;
      }
      .sub-title {
        font-family: $title-font;
        font-weight: 700;
        font-size: 18px;
        color: $title-dark-grey;
      }
      .modal-input {
        input {
          background: $light-blue;
          margin: 0;
          padding: 14px 16px;
          border: 0;
          border-radius: 5px;
          color: $title-dark-grey;
          &:focus {
            box-shadow: 0 0 5px $primary-blue;
          }
        }
      }
      .primary-button {
        position: relative;
        padding: 14px 32px;
        margin: 0;
        .loading-spinner {
          position: absolute;
          top: 12px;
          right: 0;
          width: 20px;
          height: 20px;
        }
      }
      .invite-container {
        padding-top: 48px;
        .modal-input {
          margin-top: 24px;
        }
      }
      .update-container {
        padding-top: 24px;
        padding-right: 40px;
        .edit-wrapper {
          margin-top: 40px;
          .modal-input:last-child {
            margin-top: 8px;
          }
        }
        .submit-wrapper {
          margin-top: 24px;
        }
      }
      .error-wrapper, .success-info-wrapper {
        display: flex;
        align-items: flex-start;
        position: absolute;
        left: 0;
        right: 0;
        top: 100%;
        margin-top: 11px;
        border-radius: 5px;
        z-index: 1;
      }
      .input-wrapper {
        position: relative;
        .error-wrapper {
          align-items: flex-start;
          background: $light-red;
          padding: 8px;
          .error-info {
            margin-left: 8px;
            font-size: 12px;
            color: $title-dark-grey;
          }
        }
      }
      .success-info-wrapper {
        align-items: center;
        background: #C4F8DD;
        padding: 16px;
        .vue-avatar--wrapper {
          width: 48px !important;
          height: 48px !important;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .success-content {
          margin-left: 12px;
          &-message {
            font-weight: 700;
            font-size: 16px;
            color: $title-dark-grey;
          }
          &-description {
            font-size: 12px;
            color: $title-grey;
            b {
              font-weight: 700;
              color: $title-dark-grey;
            }
          }
        }
      }
      .btn-dismiss {
        position: absolute;
        top: 11px;
        right: 8px;
        .rt-picture {
          width: 12px;
        }
      }
      .modal-separator {
        width: 100%;
        margin: 40px 0;
        border: 1px solid $light-blue6;
      }
      .teachers-wrapper {
        position: relative;
        &.empty {
          min-height: 242px;
          .teachers-list {
            border: 0;
          }
        }
        .vld-overlay {
          background: transparent;
        }
        .teachers-list {
          margin-top: 24px;
          padding: 8px 24px;
          border: 2px solid $light-blue6;
          border-radius: 8px;
          list-style: none;
          .teacher-item {
            display: flex;
            align-items: center;;
            position: relative;
            padding: 16px 0;
            border-bottom: 2px solid rgba(73, 139, 249, 0.2);
			.teacher-info {
			  margin-right: 15px;
			}
            .teacher-name {
              font-weight: 700;
              font-size: 16px;
              color: $title-dark-grey;
            }
            .teacher-email {
              font-weight: 400;
              font-size: 12px;
              color: $title-grey;
            }
            &:last-child {
              border: 0;
            }
            .invite-status {
              display: flex;
              align-items: center;
              padding: 8px 12px;
              background: $light-yellow;
			  border-radius: 5px;
              .rt-picture {
                width: 10px;
                margin-right: 8px;
              }
              .invite-status-text {
                font-weight: 700;
                font-size: 10px;
                color: $title-dark-grey;
              }
            }
            .success-info-wrapper {
              top: calc(100% - 20px);
            }
          }
        }
      }
      .btn-close {
        position: absolute;
        top: 25px;
        right: 25px;
      }
    }
  }
}
</style>
